import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Lock } from 'lucide-react';

import {
  aiCheckpointStatus,
  AiCheckpointStatus,
} from 'core/hooks/AiPortrait/aiPortraitModel';

import { T } from 'services/ThemeTokens';

import { AI_CHECKPOINT_COLORS } from './aiCheckpointColors';
import { AI_CHECKPOINT_STATUS_ICONS } from './aiPortraitIcons';

interface IAiCheckpointIconProps {
  status: string | null | undefined;
  size?: number;
  /**
   * Имя значка для чтения с экрана. Без него значок декоративный: статус
   * рядом уже сказан словами.
   */
  label?: string;
  /** ТЗ 0120: «Оплата» у роли без финансов — замок, «нет доступа». */
  noAccess?: boolean;
}

/**
 * ТЗ 0120 — значок статуса чекпоинта: зелёная галочка, жёлтый треугольник,
 * красный крест, серый пунктирный круг. Цвет — токен темы.
 */
export const AiCheckpointIcon = memo(
  ({ status, size = 16, label, noAccess = false }: IAiCheckpointIconProps) => {
    const { t } = useTranslation();
    const value = aiCheckpointStatus(status);
    const Icon = noAccess ? Lock : AI_CHECKPOINT_STATUS_ICONS[value];
    const word = noAccess
      ? t('aiPortrait.checkpointStatus.noAccess')
      : t(`aiPortrait.checkpointStatus.${value}`);
    const a11y = label
      ? {
          role: 'img',
          'aria-label': `${label}: ${word}`,
        }
      : { 'aria-hidden': true as const };
    return (
      <Glyph $status={value} $muted={noAccess} {...a11y}>
        <Icon size={size} strokeWidth={2.25} aria-hidden="true" />
      </Glyph>
    );
  },
);

AiCheckpointIcon.displayName = 'AiCheckpointIcon';

const Glyph = styled.span<{ $status: AiCheckpointStatus; $muted?: boolean }>`
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  color: ${({ $status, $muted }) =>
    $muted ? T.textFaint : AI_CHECKPOINT_COLORS[$status].fg};
`;
